import { useMessagingLive } from "../hooks/useMessagingLive";
import {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ChevronDown,
  ChevronUp,
  Maximize2,
  MessageCircle,
  Minimize2,
} from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { aiApi } from "../api/ai.api";
import { messagingApi } from "../api/messaging.api";
import type { WorkspaceReference } from "../api/references.api";
import {
  STUDENT_MESSAGING_ROLES,
  countStudentUnread,
  onStudentMessagingTarget,
  studentConversationsKey,
  studentMessagingApi,
  studentMessagingScope,
  type StudentMessagingContacts,
} from "../api/student-messaging.api";
import { useAuthStore } from "../store/auth.store";
import { onAskPermioAi } from "../views/ai/askPermioAi";
import { ConversationView } from "./messaging/Conversations";
import {
  ASSISTANT_LEGACY_PATH,
  ASSISTANT_ROLES,
  ASSISTANT_TARGET,
  MESSAGES_PATH,
  MESSAGING_ROLES,
  messagesAddress,
  parseMessagesAddress,
  useMessagingPageSlot,
  type MessagingTarget,
  type MessagingView,
} from "./messaging/full-page";
import { BugReport } from "./messaging/BugReport";
import { Inbox, mergeInbox, type InboxFilter } from "./messaging/Inbox";
import { MessagingPage } from "./messaging/MessagingPage";
import { NewMessage } from "./messaging/NewMessage";
import { BackButton, IconButton } from "./messaging/parts";
import { StudentThread } from "./messaging/StudentThread";
import {
  STUDENT_REFRESH_CLOSED_MS,
  STUDENT_REFRESH_MS,
  isRefusal,
  studentErrorMessage,
  validContacts,
} from "./messaging/student-shared";
import "./MessagingDock.css";

// Chargé à la première ouverture de la conversation avec Permio AI.
const AssistantThread = lazy(() => import("../views/ai/AssistantThread"));

/** Emplacement de la conversation avec Permio AI : le fil, gardé en vie ailleurs, vient s'y accrocher. */
function AssistantSlot({ host }: { host: HTMLElement }) {
  const place = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const parent = place.current;
    parent?.appendChild(host);
    // Détaché, le fil a perdu son défilement : il le repose en revenant à l'écran.
    host.dispatchEvent(new Event("permio:attached"));
    return () => {
      if (host.parentNode === parent) host.remove();
    };
  }, [host]);
  return <div ref={place} className="messaging-assistant-slot" />;
}

// Une seule messagerie à l'écran : les conversations de l'équipe (`/v1/messaging`, temps réel),
// celles avec les élèves (`/v1/student-messaging`, relues périodiquement) et celle avec Permio AI
// (`/v1/ai`) dans la même liste. Les API restent séparées : seul l'écran les réunit.
// Deux mises en page pour un seul état : le volet flottant, ou la pleine page sur `/messages`.
export function MessagingDock() {
  useMessagingLive();
  const location = useLocation(),
    navigate = useNavigate();
  // Pleine page : le volet flottant s'efface, la messagerie s'affiche dans l'emplacement de la page.
  const fullPage = location.pathname === MESSAGES_PATH,
    slot = useMessagingPageSlot();
  const [open, setOpen] = useState(false),
    [dockSelected, setDockSelected] = useState<MessagingTarget | null>(null),
    [creating, setCreating] = useState(false),
    // « Message à tous les élèves » ouvert directement (liste, page Apprenants) : « Retour » revient à la liste.
    [broadcastFirst, setBroadcastFirst] = useState(false),
    // Signalement d'un problème au service technique Permio : un écran, pas une conversation.
    [reporting, setReporting] = useState(false),
    [shared, setShared] = useState<WorkspaceReference | null>(null),
    [before, setBefore] = useState<number | undefined>();
  const [search, setSearch] = useState(""),
    [filter, setFilter] = useState<InboxFilter>("all");
  // Élève dont l'échange est à ouvrir (fiche de l'élève, adresse `/messages?eleve=`).
  const [dockArrival, setDockArrival] = useState<string | null>(null),
    [arrivalError, setArrivalError] = useState("");
  const user = useAuthStore((s) => s.user),
    school = useAuthStore((s) => s.activeSchoolId),
    scope = useAuthStore(studentMessagingScope),
    id = useId(),
    toggle = useRef<HTMLButtonElement>(null),
    qc = useQueryClient();
  const allowed = MESSAGING_ROLES.includes(user?.role ?? "");
  // La messagerie est à l'écran : volet déplié, ou pleine page.
  const active = open || fullPage;

  // ── Vue en cours. Pleine page : l'adresse porte la conversation ; volet : l'état ci-dessus. ──
  const address = useMemo(
    () => (fullPage ? parseMessagesAddress(location.search) : null),
    [fullPage, location.search],
  );
  const selected = address ? address.selected : dockSelected,
    arrival = address ? address.arrival : dockArrival;
  const show = useCallback(
    (view: MessagingView, replace = false) => {
      if (fullPage) navigate(messagesAddress(view), { replace });
      else {
        setDockSelected(view.selected ?? null);
        setDockArrival(view.arrival ?? null);
      }
    },
    [fullPage, navigate],
  );
  // Les écouteurs et l'ouverture différée d'un échange suivent la mise en page du moment.
  const showLatest = useRef(show);
  useEffect(() => {
    showLatest.current = show;
  }, [show]);
  // Le volet garde la vue de la pleine page : en la quittant, on y retrouve la même conversation.
  useEffect(() => {
    if (!address) return;
    setDockSelected(address.selected);
    setDockArrival(address.arrival);
  }, [address]);
  // Dernière page hors messagerie : c'est là que « Réduire » ramène. Une adresse qui redirige
  // vers la messagerie n'en est pas une : y revenir rouvrirait la pleine page.
  const outside = useRef<string | null>(null);
  useEffect(() => {
    if (
      location.pathname !== MESSAGES_PATH &&
      !location.pathname.startsWith(MESSAGES_PATH + "/") &&
      location.pathname !== ASSISTANT_LEGACY_PATH
    )
      outside.current = location.pathname + location.search + location.hash;
  }, [location]);

  const list = useQuery({
    queryKey: ["messaging-list", user?.id],
    queryFn: messagingApi.list,
    enabled: allowed,
    refetchInterval: (q) => (q.state.error ? false : active ? 5000 : 15000),
    refetchIntervalInBackground: false,
    retry: false,
    gcTime: 0,
  });
  const directory = useQuery({
    queryKey: ["messaging-directory", user?.id],
    queryFn: messagingApi.directory,
    enabled: allowed && active,
    refetchInterval: (q) => (q.state.error ? false : 30000),
    retry: false,
    gcTime: 0,
  });

  // ── Conversations avec les élèves : un refus (403, compte sans école) laisse la messagerie de l'équipe seule. ──
  const withStudents =
    allowed && STUDENT_MESSAGING_ROLES.includes(user?.role ?? "") && !!school;
  const studentKey = useMemo(() => studentConversationsKey(scope), [scope]);
  const studentList = useQuery({
    queryKey: studentKey,
    queryFn: studentMessagingApi.conversations,
    enabled: withStudents,
    retry: false,
    refetchInterval: (q) =>
      isRefusal(q.state.error)
        ? false
        : active
          ? STUDENT_REFRESH_MS
          : STUDENT_REFRESH_CLOSED_MS,
  });
  const studentContacts = useQuery({
    queryKey: ["student-messaging-contacts", scope],
    queryFn: studentMessagingApi.contacts,
    enabled: withStudents && active,
    retry: false,
  });
  const studentRefused = isRefusal(studentList.error);
  const students = useMemo(
    () =>
      !studentRefused && Array.isArray(studentList.data)
        ? studentList.data
        : [],
    [studentRefused, studentList.data],
  );
  const contacts = isRefusal(studentContacts.error)
    ? null
    : validContacts(studentContacts.data);
  const canWriteStudents =
    !!contacts &&
    (contacts.canOpenOffice ||
      contacts.canOpenDirect ||
      contacts.canCreateGroup);

  // ── Permio AI : un interlocuteur de plus, proposé quand l'assistant répond pour cette école. ──
  const withAssistant =
    allowed && ASSISTANT_ROLES.includes(user?.role ?? "") && !!school;
  // Même clé que le fil : un seul appel, un seul état.
  const assistantStatus = useQuery({
    queryKey: ["ai-status", school ?? "", user?.id ?? ""],
    queryFn: aiApi.status,
    enabled: withAssistant && active,
    retry: false,
    gcTime: 0,
  });
  // Ce que Permio a préparé de lui-même : même clé que le fil, qui en affiche le détail.
  const assistantInitiatives = useQuery({
    queryKey: ["ai-initiatives", school ?? "", user?.id ?? ""],
    queryFn: aiApi.initiatives,
    enabled: withAssistant && active && !!assistantStatus.data?.canPropose,
    refetchInterval: 60000,
    retry: false,
    gcTime: 0,
  });
  const assistantPrepared = assistantInitiatives.data?.length ?? 0;
  const assistantShown =
    withAssistant &&
    active &&
    !reporting &&
    !creating &&
    !arrival &&
    selected?.source === "assistant";
  // Le fil reste en vie une fois ouvert : replier le volet, revenir à la liste ou agrandir
  // n'interrompt ni la réponse en cours ni la discussion.
  const assistantHost = useMemo(() => {
    const node = document.createElement("div");
    node.className = "messaging-assistant-host";
    return node;
  }, []);
  const [assistantStarted, setAssistantStarted] = useState(false),
    [assistantUnread, setAssistantUnread] = useState(0),
    [assistantBusy, setAssistantBusy] = useState(false);
  useEffect(() => {
    setAssistantStarted(false);
    setAssistantUnread(0);
    setAssistantBusy(false);
  }, [school, user?.role]);
  // Après l'effet précédent : un changement d'école ou de rôle, fil à l'écran, le relance aussitôt.
  useEffect(() => {
    if (!assistantShown) return;
    setAssistantStarted(true);
    setAssistantUnread(0);
  }, [assistantShown, school, user?.role]);
  const assistant = useMemo(
    () =>
      withAssistant && (assistantStatus.data?.available || assistantStarted)
        ? {
            id: ASSISTANT_TARGET.id,
            title: "Permio AI",
            unread: assistantUnread,
            busy: assistantBusy,
            prepared: assistantPrepared,
          }
        : null,
    [
      withAssistant,
      assistantStatus.data?.available,
      assistantStarted,
      assistantUnread,
      assistantBusy,
      assistantPrepared,
    ],
  );

  useEffect(() => {
    const listener = (event: Event) => {
      const detail = (
        event as CustomEvent<{
          reference?: WorkspaceReference;
          conversationId?: string;
          sequence?: number;
        }>
      ).detail;
      setOpen(true);
      setCreating(false);
      setReporting(false);
      if (detail.reference) {
        setShared(detail.reference);
        showLatest.current({
          selected: detail.conversationId
            ? { source: "team", id: detail.conversationId }
            : null,
        });
      } else if (detail.conversationId) {
        showLatest.current({
          selected: { source: "team", id: detail.conversationId },
        });
        setBefore(detail.sequence ? detail.sequence + 1 : undefined);
      } else {
        showLatest.current({});
      }
    };
    window.addEventListener("permio:share-message", listener);
    return () => window.removeEventListener("permio:share-message", listener);
  }, []);

  // ── Fiche de l'élève : « Écrire à l’élève » ouvre le volet sur son échange, sans changer de page. ──
  useEffect(
    () =>
      onStudentMessagingTarget((target) => {
        if (!allowed) return;
        setOpen(true);
        setCreating(false);
        setReporting(false);
        setBefore(undefined);
        if (target.broadcast) {
          showLatest.current({});
          setBroadcastFirst(true);
          setCreating(true);
          return;
        }
        showLatest.current(
          target.conversationId
            ? { selected: { source: "student", id: target.conversationId } }
            : { arrival: target.candidateId ?? null },
        );
      }),
    [allowed],
  );

  // ── Accueil : « Replanifier avec Permio AI » ouvre le volet sur Permio AI et envoie la demande. ──
  const [assistantAsk, setAssistantAsk] = useState<{
    id: number;
    prompt: string;
  } | null>(null);
  useEffect(
    () =>
      onAskPermioAi((prompt) => {
        if (!withAssistant) return;
        setOpen(true);
        setCreating(false);
        setReporting(false);
        setBefore(undefined);
        showLatest.current({ selected: ASSISTANT_TARGET });
        setAssistantAsk((current) => ({ id: (current?.id ?? 0) + 1, prompt }));
      }),
    [withAssistant],
  );

  // Le secrétariat ouvre (ou crée) la boîte commune de l'élève ; un moniteur, l'échange à deux.
  const arrivalFor = useRef<string | null>(null);
  const arrive = useCallback(
    async (candidateId: string, abilities: StudentMessagingContacts) => {
      setArrivalError("");
      try {
        let found: string | undefined;
        if (abilities.canOpenOffice)
          found = (await studentMessagingApi.openOffice(candidateId)).id;
        else if (abilities.canOpenDirect)
          found = (await studentMessagingApi.openDirect(candidateId)).id;
        else {
          // Lecture seule : rien n'est créé, on retrouve la boîte commune si elle existe déjà.
          const known = await qc.ensureQueryData({
            queryKey: studentKey,
            queryFn: studentMessagingApi.conversations,
          });
          found = (Array.isArray(known) ? known : []).find(
            (c) => c.kind === "OFFICE" && c.candidateId === candidateId,
          )?.id;
        }
        if (arrivalFor.current !== candidateId) return;
        if (!found) {
          setArrivalError("Aucun échange avec cet élève pour le moment.");
          return;
        }
        void qc.invalidateQueries({ queryKey: studentKey });
        // Pleine page : l'adresse de l'élève laisse la place à celle de sa conversation.
        showLatest.current(
          { selected: { source: "student", id: found } },
          true,
        );
      } catch (error) {
        if (arrivalFor.current !== candidateId) return;
        setArrivalError(
          studentErrorMessage(error, "Vérifiez la connexion puis réessayez."),
        );
      }
    },
    [qc, studentKey],
  );
  useEffect(() => {
    if (!arrival) {
      arrivalFor.current = null;
      setArrivalError("");
      return;
    }
    if (!contacts || arrivalFor.current === arrival) return;
    arrivalFor.current = arrival;
    void arrive(arrival, contacts);
  }, [arrival, contacts, arrive]);

  const entries = useMemo(
    () =>
      mergeInbox(
        Array.isArray(list.data?.items) ? list.data.items : [],
        students,
        assistant,
      ),
    [list.data, students, assistant],
  );
  const unread =
    (Number(list.data?.unread) || 0) +
    countStudentUnread(students) +
    assistantUnread +
    assistantPrepared;
  const conversation =
    selected?.source === "team"
      ? list.data?.items?.find((c) => c.id === selected.id)
      : undefined;
  // Pourquoi l'échange avec l'élève ne s'ouvre pas : message du serveur tel quel.
  const arrivalProblem =
    arrivalError ||
    (studentContacts.isError
      ? studentErrorMessage(
          studentContacts.error,
          "Vérifiez la connexion puis réessayez.",
        )
      : !withStudents || (studentContacts.isSuccess && !contacts)
        ? "La messagerie avec les élèves n’est pas ouverte à ce compte."
        : "");
  const retryArrival =
    arrival && arrivalError && contacts
      ? () => void arrive(arrival, contacts)
      : !arrivalError && studentContacts.isError
        ? () => void studentContacts.refetch()
        : undefined;
  if (!allowed) return null;

  // ── Contenus, communs au volet et à la pleine page : état général, liste, conversation. ──
  const unavailable =
    list.isError ||
    directory.isError ||
    (!!list.data && !Array.isArray(list.data.items)) ||
    (!!directory.data &&
      (!Array.isArray(directory.data.items) ||
        !Array.isArray(directory.data.schools)));
  const ready =
    !unavailable && list.data && directory.data
      ? { list: list.data, directory: directory.data }
      : null;
  const canCreate =
    !!ready && (ready.directory.role !== "read_only" || canWriteStudents);
  const status = unavailable ? (
    <div className="messaging-state-box">
      <p role="alert">
        Messagerie indisponible. Votre accès doit être revérifié.
      </p>
      <div className="messaging-state-actions">
        <button
          className="btn btn--secondary"
          onClick={() => {
            void list.refetch();
            void directory.refetch();
          }}
        >
          Réessayer
        </button>
        <button className="btn btn--ghost" onClick={() => setReporting(true)}>
          Signaler le problème
        </button>
      </div>
    </div>
  ) : !ready ? (
    <p role="status" className="messaging-state-box">
      Chargement…
    </p>
  ) : null;
  // Permio AI ne dépend pas de la messagerie de l'équipe : son fil s'ouvre même si elle est en panne.
  const assistantDetail = assistantShown ? (
    <AssistantSlot host={assistantHost} />
  ) : null;
  // Le signalement ne dépend pas non plus de la messagerie : il sert justement quand elle est en panne.
  const reportDetail = reporting ? (
    <BugReport
      // Page concernée : celle sous le volet ; en pleine page, la dernière page ouverte avant la messagerie.
      page={() =>
        fullPage
          ? (outside.current ?? MESSAGES_PATH).split(/[?#]/)[0]
          : location.pathname
      }
      onClose={() => setReporting(false)}
    />
  ) : null;
  const detail = reportDetail ? (
    reportDetail
  ) : assistantDetail ? (
    assistantDetail
  ) : !ready ? null : arrival ? (
    <section className="messaging-state">
      <BackButton onBack={() => show({})} />
      <div className="messaging-state-box">
        {arrivalProblem ? (
          <>
            <p role="alert">
              Ouverture de l’échange impossible. {arrivalProblem}
            </p>
            {retryArrival && (
              <div className="messaging-state-actions">
                <button className="btn btn--secondary" onClick={retryArrival}>
                  Réessayer
                </button>
              </div>
            )}
          </>
        ) : (
          <p role="status">Ouverture de l’échange…</p>
        )}
      </div>
    </section>
  ) : creating ? (
    <NewMessage
      key={broadcastFirst ? "broadcast" : "new"}
      directory={ready.directory}
      contacts={contacts}
      scope={scope}
      broadcast={broadcastFirst}
      onCancel={() => setCreating(false)}
      onCreated={(target) => {
        setCreating(false);
        show({ selected: target });
        void qc.invalidateQueries({
          queryKey: target.source === "team" ? ["messaging-list"] : studentKey,
        });
      }}
    />
  ) : selected?.source === "student" ? (
    <StudentThread
      key={selected.id}
      id={selected.id}
      scope={scope}
      summary={students.find((c) => c.id === selected.id)}
      author={[user?.firstName, user?.lastName].filter(Boolean).join(" ")}
      onBack={() => show({})}
    />
  ) : selected ? (
    conversation ? (
      <ConversationView
        key={selected.id}
        conversation={conversation}
        directory={ready.directory}
        shared={shared}
        clearShared={() => setShared(null)}
        initialBefore={before}
        onBack={() => {
          show({});
          setBefore(undefined);
        }}
      />
    ) : (
      <section className="messaging-state">
        <div className="messaging-state-box">
          <p>Conversation indisponible.</p>
          <div className="messaging-state-actions">
            <button className="btn btn--ghost" onClick={() => show({})}>
              Retour
            </button>
          </div>
        </div>
      </section>
    )
  ) : null;
  const inbox = ready && (
    <Inbox
      entries={entries}
      current={creating || arrival ? null : selected}
      loading={withStudents && studentList.isLoading}
      unreadConversations={
        (Number(ready.list.unreadConversations) || 0) +
        students.filter((c) => c.unread > 0).length +
        (assistantUnread > 0 || assistantPrepared > 0 ? 1 : 0)
      }
      sharing={!!shared}
      canCreate={canCreate}
      note={
        ready.directory.role === "read_only" && !canWriteStudents
          ? "Compte en lecture seule : pas de nouveau message."
          : undefined
      }
      staleStudents={
        studentList.isError && !studentRefused
          ? () => void studentList.refetch()
          : undefined
      }
      search={search}
      onSearch={setSearch}
      filter={filter}
      onFilter={setFilter}
      page={fullPage}
      actions={
        fullPage && (
          <IconButton
            icon={Minimize2}
            label="Réduire la messagerie"
            onClick={() => {
              // Le volet a déjà la vue de la page : il se déplie sur la même conversation.
              setOpen(true);
              navigate(outside.current ?? "/today");
            }}
          />
        )
      }
      onCreate={() => {
        setReporting(false);
        setBroadcastFirst(false);
        setCreating(true);
      }}
      onBroadcast={
        contacts?.canOpenOffice
          ? () => {
              setReporting(false);
              setBroadcastFirst(true);
              setCreating(true);
            }
          : undefined
      }
      onReport={() => {
        setCreating(false);
        setReporting(true);
      }}
      onOpen={(entry) => {
        setCreating(false);
        setReporting(false);
        show({
          selected: { source: entry.source, id: entry.conversation.id },
        });
        setBefore(undefined);
      }}
    />
  );

  // Même place dans l'arbre pour le volet et la pleine page : le fil ne redémarre pas en changeant de mise en page.
  const assistantThread =
    withAssistant && assistantStarted
      ? createPortal(
          <Suspense fallback={<p role="status">Chargement…</p>}>
            <AssistantThread
              visible={assistantShown}
              onBack={() => showLatest.current({})}
              onUnseen={() => setAssistantUnread((count) => count + 1)}
              onBusy={setAssistantBusy}
              ask={assistantAsk}
            />
          </Suspense>,
          assistantHost,
        )
      : null;
  if (fullPage)
    return (
      <>
        {assistantThread}
        {slot
          ? createPortal(
              <MessagingPage
                // Messagerie de l'équipe en panne ou en chargement : son état tient dans la colonne de la liste.
                status={reportDetail || assistantDetail ? null : status}
                inbox={
                  reportDetail || assistantDetail ? (status ?? inbox) : inbox
                }
                detail={detail}
                canCreate={canCreate}
              />,
              slot,
            )
          : null}
      </>
    );
  return (
    <>
      {assistantThread}
      <aside
        className={`charte-root v2-root messaging-root messaging-dock${open ? " messaging-dock--open" : ""}`}
        aria-label="Messagerie"
        onKeyDown={(event) => {
          if (event.key === "Escape" && open) {
            setOpen(false);
            toggle.current?.focus();
          }
        }}
      >
        <button
          ref={toggle}
          type="button"
          className="messaging-dock__toggle"
          aria-expanded={open}
          aria-controls={id}
          title={open ? "Réduire la messagerie" : "Ouvrir la messagerie"}
          onClick={() => setOpen((v) => !v)}
        >
          <MessageCircle size={20} aria-hidden="true" />
          <span>Messagerie</span>
          {!list.isError && unread > 0 && (
            <strong aria-label={`${unread} messages non lus`}>{unread}</strong>
          )}
          {open ? <ChevronDown size={18} /> : <ChevronUp size={18} />}
        </button>
        {open && (
          <button
            type="button"
            className="messaging-dock__expand"
            aria-label="Agrandir la messagerie"
            title="Agrandir la messagerie"
            onClick={() =>
              navigate(
                messagesAddress({
                  selected: dockSelected,
                  arrival: dockArrival,
                }),
              )
            }
          >
            <Maximize2 size={16} aria-hidden="true" />
          </button>
        )}
        {open && (
          <div id={id} className="messaging-dock__body messaging-live">
            {reportDetail ?? assistantDetail ?? status ?? detail ?? inbox}
          </div>
        )}
      </aside>
    </>
  );
}
